import React, { useState, useEffect } from 'react';
import { Label, Mapa, AreaMapa, SearchContainer } from './style';
import GoogleMap from 'google-map-react';
import Marker from './marker';
import lupa from "../../assets/icons/icons-8-search.png";

import { store } from "react-notifications-component";
import { theme } from '../../styles/theme';


export const GOOGLE_MAPA_VERTICES = '@agroreceita_google_mapa_vertices';

const BRAZIL_CENTER_COORDINATES = { lat: -14.235004, lng: -51.925280 };

const isMobileOrTabletDevice = () => /android|iphone|ipad|ipod|windows phone/i.test((navigator.userAgent || window.opera || '').toLowerCase());

const MapaArea = ({ ...props }) => {

    const [latitude, setLatitude] = useState('');
    const [longitude, setLongitude] = useState('');
    const [onMarkerClick, setOnMarkerClick] = useState('');
    const [fullScreen, setFullScreen] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [locationDenied, setLocationDenied] = useState(false);
    const [isLoadingLocation, setIsLoadingLocation] = useState(true); // Iniciar como carregando

    // Definir coordenadas padrão do Brasil central (fallback)
    const defaultCoordinates = (props.initialLat && props.initialLng) ? {
        lat: parseFloat(props.initialLat),
        lng: parseFloat(props.initialLng)
    } : BRAZIL_CENTER_COORDINATES;

    const [coordinates, setCoordinates] = useState(defaultCoordinates);
    const [centerCoordinates, setCenterCoordinates] = useState(defaultCoordinates);
    const [fullScreenControll, setFullScreenControll] = useState(false);
    const [mapsApi, setMapsApi] = useState(null);

    const changeCoordinates = (map) => {
        setCoordinates({
            lat: map.lat,
            lng: map.lng,
        });
    }

    const handleSearch = () => {
        if (searchText.trim() !== '' && window.google) {
            const geocoder = new window.google.maps.Geocoder();
            geocoder.geocode({ address: searchText }, (results, status) => {
                if (status === 'OK' && results[0]) {
                    const location = results[0].geometry.location;
                    const newCoordinates = {
                        lat: location.lat(),
                        lng: location.lng()
                    };
                    setCoordinates(newCoordinates);
                    setCenterCoordinates(newCoordinates);
                }
            });
        }
    }

    const requestLocation = () => {
        setIsLoadingLocation(true);
        
        // Verificar primeiro se temos coordenadas iniciais válidas das props
        if (props.initialLat && props.initialLng) {
            const propsCoordinates = {
                lat: parseFloat(props.initialLat),
                lng: parseFloat(props.initialLng)
            };
            setLatitude(propsCoordinates.lat);
            setLongitude(propsCoordinates.lng);
            setCoordinates(propsCoordinates);
            setCenterCoordinates(propsCoordinates);
            setIsLoadingLocation(false);
            return; // Não solicitar localização do navegador se já temos coordenadas válidas
        }
        
        // Desktop: não solicitar geolocalização do navegador (evita popup "Acesso ao local bloqueado").
        // Tablet/celular continuam pedindo normalmente.
        if (props.skipAutoLocateOnDesktop && !isMobileOrTabletDevice()) {
            setLocationDenied(false);
            setCoordinates(BRAZIL_CENTER_COORDINATES);
            setCenterCoordinates(BRAZIL_CENTER_COORDINATES);
            setIsLoadingLocation(false);
            return;
        }

        // Se não temos coordenadas iniciais, solicitar localização do navegador
        navigator.geolocation.getCurrentPosition(
            function (position) {
                setLocationDenied(false);
                const userCoordinates = {
                    lat: position.coords.latitude,
                    lng: position.coords.longitude
                };
                
                // Sempre usar a localização real obtida
                setLatitude(userCoordinates.lat);
                setLongitude(userCoordinates.lng);
                setCoordinates(userCoordinates);
                setCenterCoordinates(userCoordinates);
                setIsLoadingLocation(false);
            },
            function (error) {
                setIsLoadingLocation(false);
                
                if (error.code === error.PERMISSION_DENIED) {
                    setLocationDenied(true);
                }
                
                // Em caso de erro, usar props se disponíveis, senão manter padrão
                if (props.initialLat && props.initialLng) {
                    const propsCoordinates = {
                        lat: parseFloat(props.initialLat),
                        lng: parseFloat(props.initialLng)
                    };
                    setLatitude(propsCoordinates.lat);
                    setLongitude(propsCoordinates.lng);
                    setCoordinates(propsCoordinates);
                    setCenterCoordinates(propsCoordinates);
                }
            },
            { 
                enableHighAccuracy: true, 
                timeout: 10000,
                maximumAge: 0 // Não usar cache, sempre buscar localização atual
            }
        );
    };

    useEffect(() => {
        // Se um endereço foi passado, vamos geocodar (useEffect abaixo) em vez de pedir localização
        if (props.endereco && props.endereco.trim()) {
            setIsLoadingLocation(false);
            return;
        }
        requestLocation();
    }, []);

    // `onGoogleApiLoaded` nem sempre dispara de forma confiável quando o script do
    // Google Maps já foi carregado por uma montagem anterior do mapa (comportamento
    // observado do google-map-react). Por segurança, também fazemos um polling em
    // window.google que nunca desiste (só para quando encontra a API ou o componente
    // desmonta), garantindo que o geocode sempre rode, mesmo sem o callback.
    useEffect(() => {
        if (mapsApi) return;

        // window.google.maps pode existir como namespace "vazio" por um instante,
        // antes das classes (Geocoder, Marker, etc.) terminarem de ser anexadas —
        // por isso checamos a classe específica, não só o namespace.
        const isGeocoderReady = () =>
            window.google && window.google.maps && typeof window.google.maps.Geocoder === 'function';

        if (isGeocoderReady()) {
            setMapsApi({ maps: window.google.maps });
            return;
        }

        const interval = setInterval(() => {
            if (isGeocoderReady()) {
                setMapsApi({ maps: window.google.maps });
                clearInterval(interval);
            }
        }, 300);

        return () => clearInterval(interval);
    }, [mapsApi]);

    // Geocodifica o endereço e centraliza o mapa nele.
    // Depende de `mapsApi` (setado via onGoogleApiLoaded, com fallback de polling acima)
    // em vez de fazer polling com desistência, que competia com o carregamento do mapa
    // e podia desistir silenciosamente antes do script terminar de carregar.
    useEffect(() => {
        const endereco = props.endereco && props.endereco.trim();
        if (!endereco || !mapsApi) return;

        let cancelled = false;

        const geocoder = new mapsApi.maps.Geocoder();
        geocoder.geocode({ address: endereco }, (results, status) => {
            if (cancelled) return;
            if (status === 'OK' && results[0]) {
                const location = results[0].geometry.location;
                const coords = { lat: location.lat(), lng: location.lng() };
                setCoordinates(coords);
                setCenterCoordinates(coords);
                setIsLoadingLocation(false);
            }
        });

        return () => {
            cancelled = true;
        };
    }, [props.endereco, mapsApi]);

    useEffect(() => {
        // Notificar mudanças de coordenadas para o componente pai
        if (!isLoadingLocation) {
            props.onChange(coordinates.lat, coordinates.lng);
        }
    }, [coordinates, isLoadingLocation]);

    useEffect(() => {
        // Se temos coordenadas iniciais válidas E não são zero, usar elas
        if (props.initialLat && props.initialLng &&
            parseFloat(props.initialLat) !== 0 && parseFloat(props.initialLng) !== 0) {
            const savedCoordinates = {
                lat: parseFloat(props.initialLat),
                lng: parseFloat(props.initialLng)
            };
            setLatitude(savedCoordinates.lat);
            setLongitude(savedCoordinates.lng);
            setCoordinates(savedCoordinates);
            setCenterCoordinates(savedCoordinates);
            setIsLoadingLocation(false);
        } else if (!(props.endereco && props.endereco.trim())) {
            // Sem coordenadas salvas e sem endereço: buscar localização atual do navegador.
            // Se há endereço, o geocode (useEffect acima) cuida de centralizar o mapa —
            // chamar requestLocation() aqui competiria com o geocode e podia sobrescrever
            // as coordenadas do CEP com a localização real do dispositivo.
            requestLocation();
        }
    }, [props.initialLat, props.initialLng]);

    setTimeout(() => {
        var btnFullScreen = document.getElementsByClassName('gm-control-active gm-fullscreen-control');
        if (btnFullScreen.length > 0) {
            if (fullScreenControll === false) {
                setFullScreenControll(true);
            }
            btnFullScreen[0].onclick = function () {
                setFullScreen(!fullScreen);
            }
        }
    }, 3000);

    return (
        <>
            <AreaMapa fullScreen={fullScreen}>
                <SearchContainer>
                    <input
                        type="text"
                        placeholder="Buscar localização..."
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    />
                    <div className="search-button" onClick={handleSearch}>
                        <img src={lupa} alt="Buscar" />
                    </div>
                </SearchContainer>

                <Label
                    erro={false}
                    isFocus={true}
                    existValue={true}
                    isDisabled={false}
                    isDate={false}
                >
                    {props.label}
                </Label>

                <GoogleMap
                    bootstrapURLKeys={{
                        key: 'AIzaSyAn3UaVotrho9E6f_BWtW4wDshxnEmM8LI',
                        libraries: ['places', 'drawing'],
                        language: 'pt',
                    }}
                    center={centerCoordinates}
                    defaultZoom={17}
                    yesIWantToUseGoogleMapApiInternals
                    onGoogleApiLoaded={({ map, maps }) => setMapsApi({ map, maps })}
                    options={{
                        streetViewControl: false,
                        scaleControl: true,
                        fullscreenControl: fullScreenControll,
                        mapTypeControl: true,
                        mapTypeId: 'hybrid',  //Nomes de rua habilitados por padrão
                        mapTypeControlOptions: true,
                        panControl: true,
                        zoomControl: true,
                        overviewMapControl: true,
                        rotateControl: true,
                        draggableCursor: 'default',
                        gestureHandling: ('ontouchstart' in window || navigator.maxTouchPoints > 0) ? 'cooperative' : 'greedy',

                        //show places
                        styles: [{
                            "featureType": "all",
                            "elementType": "labels",
                            "stylers": [{
                                "visibility": "#on"
                            }]
                        }]
                    }}
                    onClick={(map) => changeCoordinates(map)}
                >
                    <Marker
                        fullScreen={fullScreen}
                        lat={coordinates.lat}
                        lng={coordinates.lng}
                        color={theme.colors.green.Verde_AgroReceita}
                    />
                </GoogleMap>
            </AreaMapa>
        </>
    )
}

export default MapaArea;